import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { z } from 'zod';
import { notifyAPIClient } from '../../utils/toast';
import { apiPost } from '../../utils/apiClient';
import {
  CreateCoachRequest,
  CreateCoachResponse,
} from '../../types/CoachTable.type';

type CreateCoachDialogProps = {
  open: boolean;
  handleClose: () => void;
  fetchCoaches: () => void;
};

type FormErrors = {
  name?: string;
  email?: string;
  password?: string;
};

const CreateCoachDialog = ({
  open,
  handleClose,
  fetchCoaches,
}: CreateCoachDialogProps) => {
  const CreateCoachInput = z.object({
    name: z
      .string()
      .min(1, 'Please enter coach name')
      .refine((val) => val.trim().length > 0, {
        message: 'Coach name cannot be just whitespace',
      }),
    email: z
      .string()
      .min(1, 'Please enter email')
      .email('Invalid email format'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(16, 'Password must not exceed 16 characters'),
  });

  type CreateCoachInputType = z.infer<typeof CreateCoachInput>;

  const [formErrors, setFormErrors] = React.useState<FormErrors>({});
  const [showPassword, setShowPassword] = React.useState(false);

  const handleConfirm = async (data: CreateCoachInputType) => {
    try {
      const createRes = await apiPost<CreateCoachResponse, CreateCoachRequest>(
        '/auth/coaches',
        data,
      );
      notifyAPIClient(createRes.message);
      fetchCoaches();
      handleClose();
      // Reset form errors
      setFormErrors({});
    } catch (error) {
      notifyAPIClient('An unexpected error occurred while creating the coach.');
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = CreateCoachInput.parse({
        name: formData.get('name'),
        email: formData.get('email'),
        password: formData.get('password'),
      });
      setFormErrors({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          fieldErrors[field] = err.message;
        });
        setFormErrors(fieldErrors);
      }
    }
  };

  const handleCloseDialog = () => {
    setFormErrors({});
    setShowPassword(false);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>Create New Coach</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          name="name"
          label="Coach Name"
          type="text"
          fullWidth
          variant="standard"
          error={!!formErrors.name}
          helperText={formErrors.name}
        />
        <TextField
          required
          margin="dense"
          id="email"
          name="email"
          label="Email Address"
          type="email"
          fullWidth
          variant="standard"
          error={!!formErrors.email}
          helperText={formErrors.email}
        />
        <TextField
          required
          margin="dense"
          id="password"
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          variant="standard"
          error={!!formErrors.password}
          helperText={formErrors.password || 'Password must be 8-16 characters'}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button type="submit" variant="contained">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCoachDialog;
